import { ICreatePersona } from '../interfaces/self-serve/responses/icreate-persona';
import { IPersonaLocalstorage } from '../interfaces/self-serve/responses/Ipersona-localstorage';
import { CurrentTeamRelationship } from '../interfaces/icurrent-team-relationship';
import { ICurrentUser } from '../interfaces/icurrent-user';
import { IRole } from '../interfaces/irole';
import { SimulationConstants } from './constants/self-serve/simulation.constants';
import { AssignmentConstants } from './constants/self-serve/assignment.constants';
import { IAccountInfo } from '../interfaces/iaccount-info';

export enum LearnerDashboardAssignmentFilterKey {
  ASSIGNMENT_STATUS = 'learner_dashboard_assignment_status_filter',
  ASSIGNMENT_TYPE = 'learner_dashboard_assignment_type_filter',
}

/** Returns an array of strings containing the roles names from the user */
export const getLocalStorageRoleNames = (): string[] => {
  const storedRoles = localStorage.getItem('roles');

  if (storedRoles) {
    try {
      const rolesArray = JSON.parse(storedRoles);
      if (Array.isArray(rolesArray)) {
        return rolesArray.map((role: IRole) => role.name);
      } else {
        console.error('Stored roles is not an array:', rolesArray);
        return [];
      }
    } catch (error) {
      console.error('Error parsing stored roles JSON:', storedRoles, error);
      return [];
    }
  } else {
    return [];
  }
};

export const getAccountNameByUuid = (uuid: string): string => {
  const storedAccounts = localStorage.getItem('accounts');
  if (uuid && storedAccounts) {
    try {
      const accountsArray = JSON.parse(storedAccounts);
      if (Array.isArray(accountsArray)) {
        const account = accountsArray.find(
          (account: any) => account.uuid === uuid
        );
        if (account) {
          return account.name;
        } else {
          console.error('Account not found:', uuid);
          return '';
        }
      } else {
        console.error('Stored accounts is not an array:', accountsArray);
        return '';
      }
    } catch (error) {
      console.error(
        'Error parsing stored accounts JSON:',
        storedAccounts,
        error
      );
      return '';
    }
  } else {
    return '';
  }
};

// New check as the TeamAdmin role is deprecated now.
export const isTeamManager = (): boolean => {
  const relationshipsData = localStorage.getItem('relationshipsInTeams');
  if (!relationshipsData) {
    return false;
  }

  try {
    const relationshipsInTeams = JSON.parse(relationshipsData) as {
      teamRelationships?: CurrentTeamRelationship[];
    };

    return (
      relationshipsInTeams.teamRelationships?.some((team) =>
        team.relationships.includes('MANAGER')
      ) ?? false
    );
  } catch (error) {
    console.error(
      'Failed to parse relationshipsInTeams from localStorage:',
      error
    );
    return false;
  }
};

export const SelfServeLocalStorageUtil = {
  getSelectedSelfServeAccountUuid(): string {
    const selected_account_uuid = localStorage.getItem(
      'selected_self_serve_account_uuid'
    );
    return selected_account_uuid ?? '';
  },

  setSelectedSelfServeAccountUuid(uuid: string): void {
    localStorage.setItem('selected_self_serve_account_uuid', uuid);
  },

  getAccountsData(): string {
    const accounts_data = localStorage.getItem('accounts');
    return accounts_data ?? '';
  },

  getCurrentAccountUuid(): string {
    const current_account_uuid = localStorage.getItem('current_account_uuid');
    return current_account_uuid ?? '';
  },

  getCurrentUser(): ICurrentUser {
    const firstName = localStorage.getItem('user_first_name') || '';
    const lastName = localStorage.getItem('user_last_name') || '';
    const uuid = localStorage.getItem('user_uuid') || '';

    const currentUser: ICurrentUser = {
      firstName,
      lastName,
      userUuid: uuid,
      fullName: `${firstName} ${lastName}`.trim() || undefined,
    };

    return currentUser;
  },

  setPersonaByCallUuid(callUuid: string, newPersona: ICreatePersona): void {
    const key = SimulationConstants.SELFSERVE_LOCAL_STORAGE_PERSONA_INFO;
    let personaList: IPersonaLocalstorage[] = [];

    try {
      const rawData = localStorage.getItem(key);
      if (rawData?.trim()) {
        const parsed = JSON.parse(rawData);
        if (Array.isArray(parsed)) {
          personaList = parsed;
        }
      }
    } catch (err) {
      console.error('Failed to parse existing personas:', err);
    }

    const index = personaList.findIndex((p) => p.callUuid === callUuid);

    if (index !== -1) {
      personaList[index].persona = newPersona;
    } else {
      personaList.push({ callUuid, persona: newPersona });
    }

    localStorage.setItem(key, JSON.stringify(personaList));
  },

  getPersonaByCallUuid(callUuid: string): ICreatePersona | null {
    const key = SimulationConstants.SELFSERVE_LOCAL_STORAGE_PERSONA_INFO;
    try {
      const rawData = localStorage.getItem(key);
      if (!rawData?.trim()) return null;

      const parsed: IPersonaLocalstorage[] = JSON.parse(rawData);

      const entry = parsed.find((p) => p.callUuid === callUuid);
      if (!entry) return null;

      // Convert ISO strings to Date objects
      entry.persona.createdAt = new Date(entry.persona.createdAt);
      entry.persona.updatedAt = new Date(entry.persona.updatedAt);

      return entry.persona;
    } catch (err) {
      console.error('Failed to parse persona data:', err);
      return null;
    }
  },

  removePersonaByCallUuid(callUuid: string): void {
    const key = SimulationConstants.SELFSERVE_LOCAL_STORAGE_PERSONA_INFO;

    try {
      const rawData = localStorage.getItem(key);
      if (!rawData?.trim()) return;

      const parsed: IPersonaLocalstorage[] = JSON.parse(rawData);

      const filtered = parsed.filter((p) => p.callUuid !== callUuid);

      // Only update if something was actually removed
      if (filtered.length !== parsed.length) {
        localStorage.setItem(key, JSON.stringify(filtered));
      }
    } catch (err) {
      console.error('Failed to remove persona:', err);
    }
  },

  getTransformedTeamsWithUsers(): any[] {
    const key = AssignmentConstants.SELFSERVE_TRANSPORMED_TEAMS_WITH_USERS;
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to parse teams with users:', error);
      return [];
    }
  },

  setTransformedTeamsWithUsers(data: string): void {
    const key = AssignmentConstants.SELFSERVE_TRANSPORMED_TEAMS_WITH_USERS;
    try {
      const sanitizedData = data?.trim() || '[]';
      JSON.parse(sanitizedData);
      localStorage.setItem(key, sanitizedData);
    } catch (error) {
      console.error('Invalid data format for teams with users:', error);
    }
  },

  getUserRoleNames(): string[] {
    try {
      const userRolesStr = localStorage.getItem('roles');
      if (!userRolesStr) {
        console.warn('User roles not found');
        return [];
      }
      const userRoles = JSON.parse(userRolesStr);
      return userRoles.map((role: any) => role.name);
    } catch (error) {
      console.error('Error parsing user roles:', error);
      return [];
    }
  },

  accountHasRole(account: IAccountInfo, roleName: string): boolean {
    return account?.roles?.includes(roleName) ?? false;
  },

  filterAccountsByTab(
    storedAccounts: IAccountInfo[],
    tabType: 'assignments' | 'simulations'
  ): IAccountInfo[] {
    try {
      const filteredAccounts = storedAccounts.filter(
        (account: IAccountInfo) => {
          if (!account || !account.roles) {
            return false;
          }

          if (tabType === AssignmentConstants.ASSIGNMENTS_TAB) {
            // For Assignments tab: show accounts that have AssignmentManager role
            return this.accountHasRole(account, 'AssignmentManager');
          } else if (tabType === AssignmentConstants.SIMULATIONS_TAB) {
            // For Simulations tab: show accounts that have Author OR AssignmentManager role
            return (
              this.accountHasRole(account, 'Author') ||
              this.accountHasRole(account, 'AssignmentManager')
            );
          } else {
            return false;
          }
        }
      );

      // Ensure unique accounts by UUID
      const uniqueAccounts = filteredAccounts.filter(
        (account, index, self) =>
          index === self.findIndex((a) => a.uuid === account.uuid)
      );

      const sortedAccounts = uniqueAccounts.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      return sortedAccounts;
    } catch (error) {
      console.error(`Error filtering accounts for ${tabType} tab:`, error);
      return [];
    }
  },

  getSelectedAccountRoles(accountUuid: string): {
    hasAuthor: boolean;
    hasAssignmentManager: boolean;
  } {
    try {
      const accountsStr = localStorage.getItem('accounts');

      if (!accountsStr) {
        return { hasAuthor: false, hasAssignmentManager: false };
      }

      const accounts = JSON.parse(accountsStr);
      const selectedAccount = accounts.find(
        (account: any) => account.uuid === accountUuid
      );

      if (!selectedAccount || !selectedAccount.roles) {
        return { hasAuthor: false, hasAssignmentManager: false };
      }

      return {
        hasAuthor: selectedAccount.roles.includes('Author'),
        hasAssignmentManager:
          selectedAccount.roles.includes('AssignmentManager'),
      };
    } catch (error) {
      console.error('Error getting selected account roles:', error);
      return { hasAuthor: false, hasAssignmentManager: false };
    }
  },

  hasSimulationBuilderAccess(): boolean {
    try {
      const accountsStr = localStorage.getItem('accounts');

      if (!accountsStr) {
        return false;
      }

      const accounts = JSON.parse(accountsStr);

      // Check if there's at least one account with Author or AssignmentManager role
      return accounts.some((account: any) => {
        if (!account || !account.roles) {
          return false;
        }
        return (
          account.roles.includes('Author') ||
          account.roles.includes('AssignmentManager')
        );
      });
    } catch (error) {
      console.error('Error checking simulation builder access:', error);
      return false;
    }
  },

  hasSingleEligibleAccount(): boolean {
    try {
      const accountsStr = localStorage.getItem('accounts');

      if (!accountsStr) {
        return false;
      }

      const accounts = JSON.parse(accountsStr);

      const eligibleAccounts = accounts.filter((account: any) => {
        if (!account || !account.roles) {
          return false;
        }
        return (
          account.roles.includes('Author') ||
          account.roles.includes('AssignmentManager')
        );
      });

      // Check if there's exactly one eligible account
      return Array.isArray(eligibleAccounts) && eligibleAccounts.length === 1;
    } catch (error) {
      console.error(
        'Error checking if user has only one eligible account:',
        error
      );
      return false;
    }
  },

   getSelectedStatusFilters(defaultValues: string[]): string[] {
    try {
      const saved = localStorage.getItem('selectedStatusValues');
      if (!saved) return defaultValues;
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : defaultValues;
    } catch (err) {
      console.error("Failed to load selectedStatusValues:", err);
      return defaultValues;
    }
  },

  setSelectedStatusFilters(statuses: string[]): void {
    try {
      localStorage.setItem('selectedStatusValues', JSON.stringify(statuses));
    } catch (err) {
      console.error("Failed to save selectedStatusValues:", err);
    }
  },

  clearSelectedStatusFilters(): void {
    try {
      localStorage.removeItem('selectedStatusValues');
    } catch (err) {
      console.error("Failed to clear selectedStatusValues:", err);
    }
  },
};

export const LeaderboardLocalStorageUtil = {
  getSelectedAssignmentUuid(): string {
    try {
      const stored = localStorage.getItem('leaderboard_selected_assignment_uuid');
      return stored ?? '';
    } catch (error) {
      console.error('Error getting selected assignment UUID from localStorage:', error);
      return '';
    }
  },

  setSelectedAssignmentUuid(uuid: string): void {
    try {
      localStorage.setItem('leaderboard_selected_assignment_uuid', uuid);
    } catch (error) {
      console.error('Error setting selected assignment UUID in localStorage:', error);
    }
  },

  getSelectedSimulationUuid(): string {
    try {
      const stored = localStorage.getItem('leaderboard_selected_simulation_uuid');
      return stored ?? '';
    } catch (error) {
      console.error('Error getting selected simulation UUID from localStorage:', error);
      return '';
    }
  },

  setSelectedSimulationUuid(uuid: string): void {
    try {
      localStorage.setItem('leaderboard_selected_simulation_uuid', uuid);
    } catch (error) {
      console.error('Error setting selected simulation UUID in localStorage:', error);
    }
  },

  clearSelectedAssignmentUuid(): void {
    try {
      localStorage.removeItem('leaderboard_selected_assignment_uuid');
    } catch (error) {
      console.error('Error clearing selected assignment UUID from localStorage:', error);
    }
  },

  clearSelectedSimulationUuid(): void {
    try {
      localStorage.removeItem('leaderboard_selected_simulation_uuid');
    } catch (error) {
      console.error('Error clearing selected simulation UUID from localStorage:', error);
    }
  },

  clearAllLeaderboardSelections(): void {
    this.clearSelectedAssignmentUuid();
    this.clearSelectedSimulationUuid();
  },
};

export const LearnerDashboardLocalStorageUtil = {
  getSelectedAssignmentFilter(key: LearnerDashboardAssignmentFilterKey): string[] {
    try {
      const saved = localStorage.getItem(key);
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error(`Failed to load ${key} from localStorage:`, err);
      return [];
    }
  },

  setSelectedAssignmentFilter(key: LearnerDashboardAssignmentFilterKey, values: string[]): void {
    try {
      localStorage.setItem(key, JSON.stringify(values));
    } catch (err) {
      console.error(`Failed to save ${key} in localStorage:`, err);
    }
  }
};
